import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const storageKey = (param: string) => `pulse.group-filter.${param}`;

const readStoredGroups = (param: string): string[] => {
  try {
    const raw = globalThis.localStorage?.getItem(storageKey(param));
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((value): value is string => typeof value === 'string')
      : [];
  } catch {
    return [];
  }
};

const writeStoredGroups = (param: string, groups: string[]) => {
  try {
    if (groups.length === 0) {
      globalThis.localStorage?.removeItem(storageKey(param));
    } else {
      globalThis.localStorage?.setItem(
        storageKey(param),
        JSON.stringify(groups),
      );
    }
  } catch {
    // Ignore storage failures (e.g. private mode); the URL still holds state.
  }
};

/**
 * Shared entity-owner group filter backed by a query parameter
 * (e.g. `?teams=team-a,team-b`). Used by the track and check detail pages so
 * the selection is shareable and survives refreshes.
 *
 * The last selection is also persisted to `localStorage`, so when the page is
 * opened without an explicit `teams` param the user's previous selection is
 * restored. Clearing the filter removes the stored value, so a cleared filter
 * stays cleared.
 *
 * @param param - The query-parameter name to read/write. Defaults to `teams`.
 */
export const useGroupFilter = (param: string = 'teams') => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const selectedGroups = Array.from(
    new Set(
      (searchParams.get(param) ?? '')
        .split(',')
        .map(group => group.trim())
        .filter(Boolean),
    ),
  );

  const hasUrlParam = searchParams.get(param) !== null;

  const setSelectedGroups = (groups: string[], replace = false) => {
    writeStoredGroups(param, groups);
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete(param);
    const remainingQuery = nextSearchParams.toString();
    const groupsQuery = groups.map(encodeURIComponent).join(',');
    const nextQuery = [
      remainingQuery,
      groupsQuery ? `${param}=${groupsQuery}` : '',
    ]
      .filter(Boolean)
      .join('&');
    navigate({ search: nextQuery ? `?${nextQuery}` : '' }, { replace });
  };

  // Restore the last selection from storage when the URL carries no explicit
  // selection. Runs once, and only when nothing is set via the URL.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) {
      return;
    }
    hydratedRef.current = true;
    if (!hasUrlParam) {
      const stored = readStoredGroups(param);
      if (stored.length > 0) {
        setSelectedGroups(stored, true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { selectedGroups, setSelectedGroups };
};
